'use client'

import { useAtomValue, useSetAtom } from 'jotai/react'
import { Check, Shield } from 'lucide-react'
import Image from 'next/image'
import { Button } from '@/src/components/ui/button'
import { walletAccountsAtom } from '@/src/stores/accounts/wallets'
import {
  onboardingDialogAtom,
  openOnboardingDialog,
} from '@/src/stores/onboarding'

/**
 * Privacy UI version of LoginIntro with badges and new styling
 * Only shown when privacyUi feature flag is enabled
 */
export function PrivacyLoginIntro({ isLoggedIn }: { isLoggedIn: boolean }) {
  const connectedWallets = useAtomValue(walletAccountsAtom)
  const hasWallets = (connectedWallets?.groupedAccounts?.length ?? 0) > 0

  // If logged in with wallets, don't show anything (they're already set up)
  if (isLoggedIn && hasWallets) {
    return null
  }

  // If logged in but no wallets, show only connect wallet CTA
  if (isLoggedIn && !hasWallets) {
    return <LoggedInNoWalletsCTA />
  }

  // If not logged in, show both CTAs
  return <GuestModeIntro />
}

function GuestModeIntro() {
  const setOnboardingDialog = useSetAtom(onboardingDialogAtom)

  return (
    <div className='flex flex-col gap-16 items-center justify-between pt-12'>
      <h6 className='text-5xl font-serif font-extralight text-center'>
        Start your trading <span className='italic'>adventure</span>.
      </h6>
      <div className='flex flex-col sm:flex-row items-center gap-6 max-w-[960px] mx-auto'>
        <div className='flex flex-col w-[446px] p-8 rounded-3xl relative h-[512px] overflow-hidden'>
          <div className='flex flex-col gap-6 relative z-10 items-center h-full justify-between'>
            <div className='flex flex-col gap-3 items-center'>
              <div className='flex min-w-6 min-h-6 pl-1 pr-2 py-1 justify-center items-center gap-0.5 rounded-full bg-[#F8F6F2]'>
                <div className='relative w-4 h-4'>
                  <Shield className='w-4 h-4 text-success-text' />
                  <Check
                    className='w-2.5 h-2.5 text-success-text absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
                    strokeWidth={3}
                  />
                </div>
                <span className='text-xs font-medium text-[#1A1A1A]'>
                  Private
                </span>
              </div>
              <div className='flex flex-col gap-3 text-center'>
                <span className='text-xl font-semibold'>
                  Build your portfolio
                </span>
                <span>
                  Enter the world of private finance, with intelligent insights
                  and more.
                </span>
              </div>
            </div>
            <Button
              type='button'
              variant='primary'
              className='font-semibold min-w-12 min-h-12 py-3 px-4 gap-2 rounded-full w-full'
              onClick={() => {
                setOnboardingDialog(openOnboardingDialog('login'))
              }}
            >
              Sign up for free
            </Button>
          </div>
          <Image
            src='https://s3.polaris.app/artworks/girl-backpack-field-house.png'
            alt=''
            fill
            className='object-cover'
            sizes='100vw'
          />
        </div>
        <span className='text-sm text-secondary'>OR</span>
        <div className='flex flex-col w-[446px] p-8 rounded-3xl relative h-[512px] overflow-hidden'>
          <div className='flex flex-col gap-6 relative z-10 items-center h-full justify-between'>
            <div className='flex flex-col gap-3 items-center'>
              <div className='flex min-w-6 min-h-6 pl-1 pr-2 py-1 justify-center items-center gap-0.5 rounded-full bg-white/[0.12] backdrop-blur-md'>
                <Shield className='w-4 h-4 text-tertiary' />
                <span className='text-xs font-medium text-white'>Public</span>
              </div>
              <div className='flex flex-col gap-3 text-center'>
                <span className='text-xl font-semibold'>
                  Start without an account
                </span>
                <span>
                  Connect and start trading instantly. Polaris won&apos;t
                  remember you or your wallets.
                </span>
              </div>
            </div>
            <Button
              type='button'
              variant='primary'
              className='font-semibold min-w-12 min-h-12 py-3 px-4 gap-2 rounded-full w-full bg-gray-100 hover:bg-gray-200'
              onClick={() => {
                setOnboardingDialog(openOnboardingDialog('wallets'))
              }}
            >
              Connect wallets
            </Button>
          </div>
          <Image
            src='https://s3.polaris.app/artworks/girl-backpack-camping.png'
            alt=''
            fill
            className='object-cover'
            sizes='100vw'
          />
        </div>
      </div>
    </div>
  )
}

function LoggedInNoWalletsCTA() {
  const setOnboardingDialog = useSetAtom(onboardingDialogAtom)

  return (
    <div className='relative max-w-4xl w-full mx-auto flex sm:flex-row flex-col items-center rounded-3xl overflow-hidden h-[456px] sm:h-[228px] mt-12'>
      <div className='relative z-10 flex flex-col gap-6 p-6 sm:p-8 sm:pr-0 w-full max-sm:h-[calc(100%-128px)] sm:w-[calc(50%+128px)] bg-gradient-to-b sm:bg-gradient-to-r from-foreground via-80% via-foreground to-transparent'>
        <div className='flex flex-col gap-3'>
          <span className='text-xl font-semibold'>Connect your wallets</span>
          <span className='text-secondary max-w-[381px]'>
            Connect and start trading instantly. Deposit funds to enjoy fully
            private trading.
          </span>
        </div>
        <Button
          variant='primary'
          className='font-semibold'
          onClick={() => {
            setOnboardingDialog(openOnboardingDialog('wallets'))
          }}
        >
          Connect wallets
        </Button>
      </div>
      <Image
        src='https://s3.polaris.app/artworks/girl-backpack-camping.png'
        alt=''
        width={1230}
        height={1230}
        className='object-cover absolute w-full bottom-0 sm:right-0 sm:-bottom-12 sm:w-1/2'
      />
    </div>
  )
}
